import { fDate } from '@/utils/format-date'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { Calendar, User, Hash, MessageCircle, Globe } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

import { Program, ProgramStatus } from './program.types'

type Props = {
  program: Program
}

const getStatusBadge = (status: ProgramStatus) => {
  switch (status) {
    case ProgramStatus.LIVE:
      return (
        <Badge className="bg-red-100 text-red-800 hover:bg-red-100">
          ถ่ายทอดสด
        </Badge>
      )

    case ProgramStatus.PENDING:
      return (
        <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100">
          รอดำเนินการ
        </Badge>
      )
    default:
      return <Badge variant="secondary">{status}</Badge>
  }
}

export default function ProgramDetailCard({ program }: Props) {
  return (
    <Card>
      <CardHeader className="px-4 sm:px-6">
        <CardTitle className="text-base sm:text-lg font-semibold text-black">
          ข้อมูลโปรแกรม
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 sm:space-y-6">
        {/* Title and Status */}
        <div className="space-y-2">
          <div className="flex items-start justify-between gap-3">
            <h3 className="text-lg font-semibold text-black truncate flex-1">
              {program.title}
            </h3>
            {getStatusBadge(program.status)}
          </div>

          {/* Enabled Status */}
          <div className="flex items-center gap-2">
            <Badge variant={program.isEnabled ? 'default' : 'secondary'}>
              {program.isEnabled ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}
            </Badge>
          </div>
        </div>

        <Separator />

        {/* Program Details */}
        <div className="space-y-4">
          {/* Owner */}
          <div className="flex items-center gap-3">
            <User className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-sm text-muted-foreground">เจ้าของโปรแกรม</p>
              <p className="text-sm font-medium text-black truncate">
                {program.owner?.displayName ||
                  program.owner?.firstName + ' ' + program.owner?.lastName ||
                  'ไม่ระบุ'}
              </p>
            </div>
          </div>

          {/* Channel Name */}
          {program.channelName && (
            <div className="flex items-center gap-3">
              <Hash className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-sm text-muted-foreground">ชื่อช่อง</p>
                <p className="text-sm font-medium text-black truncate">
                  {program.channelName}
                </p>
              </div>
            </div>
          )}

          {/* Published Date */}
          {program.publishedAt && (
            <div className="flex items-center gap-3">
              <Calendar className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-sm text-muted-foreground">วันที่เผยแพร่</p>
                <p className="text-sm font-medium text-black">
                  {fDate(program.publishedAt)}
                </p>
              </div>
            </div>
          )}

          {/* Created Date */}
          <div className="flex items-center gap-3">
            <Calendar className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-sm text-muted-foreground">วันที่สร้าง</p>
              <p className="text-sm font-medium text-black">
                {fDate(program.createdAt)}
              </p>
            </div>
          </div>
        </div>

        <Separator />

        {/* Settings */}
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-black">การตั้งค่า</h4>
          <div className="flex flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Globe className="h-4 w-4 text-muted-foreground" />
              <Badge variant={program.isPublic ? 'default' : 'secondary'}>
                {program.isPublic ? 'เปิดสาธารณะ' : 'ส่วนตัว'}
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              <MessageCircle className="h-4 w-4 text-muted-foreground" />
              <Badge
                variant={program.isCommentActive ? 'default' : 'secondary'}
              >
                {program.isCommentActive ? 'เปิดคอมเม้นต์' : 'ปิดคอมเม้นต์'}
              </Badge>
            </div>
          </div>
        </div>

        {/* Tags */}
        {program.tags && program.tags.length > 0 && (
          <>
            <Separator />
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-black">แท็ก</h4>
              <div className="flex flex-wrap gap-2">
                {program.tags.map((tag, index) => (
                  <Badge key={index} variant="outline">
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Description */}
        {program.description && (
          <>
            <Separator />
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-black">รายละเอียด</h4>
              <div
                className="text-sm text-muted-foreground prose prose-sm max-w-none"
                // eslint-disable-next-line react-dom/no-dangerously-set-innerhtml
                dangerouslySetInnerHTML={{ __html: program.description }}
              />
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}
